import React, { useState, useEffect } from 'react';
import format from "date-fns/format";
import getDay from "date-fns/getDay";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button, Form, Modal, Table } from "react-bootstrap";
import toast from 'react-hot-toast';

const locales = {
    "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const timeSlots = [];
for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
        const startTime = new Date(0, 0, 0, hour, minute).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
        const endTime = new Date(0, 0, 0, hour, minute + 30).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
        timeSlots.push(`${startTime} - ${endTime}`);
    }
}

const minDate = new Date(2026, 0, 15);
const maxDate = new Date(2026, 2, 30);

const ScheduleCalender = ({ formData, setFormData, error }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState("");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [showSkipAlert, setShowSkipAlert] = useState(false);

    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part to compare dates only
        
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0); // Reset time part to compare dates only
        
        return compareDate < today || compareDate < minDate || compareDate > maxDate || date.getDay() === 0 || date.getDay() === 6;
    };

    const dayPropGetter = (date) => {
        if (isDateDisabled(date)) {
            return {
                className: "disabled-day",
                style: { backgroundColor: "#f8d7da", cursor: "not-allowed" },
            };
        }
        return {};
    };


    const getMonthSlots = (date) => {
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        return formData.selectedTimeSlots.filter(
            slot => {
                const slotDate = new Date(slot.date);
                return slotDate >= monthStart && slotDate <= monthEnd;
            }
        );
    };

    const handleSelectSlot = (slotInfo) => {
        const selectedDate = new Date(slotInfo.start);
        if (!isDateDisabled(selectedDate)) {
            const monthSlots = getMonthSlots(selectedDate);
            if (monthSlots.length >= 8) {
                alert("Maximum of 8 slots per month reached.");
                return;
            }
            setSelectedDate(selectedDate);
            setShowModal(true);
        }
    };

    const handleAddTimeSlot = () => {
        if (selectedTime && selectedDate) {
            const [startTime, endTime] = selectedTime.split(" - ");

            const newSlot = {
                date: selectedDate,
                startTime: startTime,
                endTime: endTime,
                duration: 30,
                dateTime: `${selectedDate.toDateString()}-${startTime}`,
            };

            // Check if slot already exists
            const slotExists = formData.selectedTimeSlots.some(
                (slot) => slot.dateTime === newSlot.dateTime
            );

            if (!slotExists) {
                // Check slots per day
                const slotsOnDate = formData.selectedTimeSlots.filter(
                    (slot) => new Date(slot.date).toDateString() === selectedDate.toDateString()
                ).length;

                if (slotsOnDate >= 2) {
                    alert("Maximum of 2 slots per day allowed.");
                    return;
                }

                // Check slots per month
                const monthSlots = getMonthSlots(selectedDate);
                if (monthSlots.length >= 8) {
                    alert("Maximum of 8 slots per month allowed.");
                    return;
                }

                setFormData((prevFormData) => ({
                    ...prevFormData,
                    selectedTimeSlots: [...prevFormData.selectedTimeSlots, newSlot],
                }));

                // Check if month is complete
                if (monthSlots.length + 1 === 8) {
                    const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
                    if (nextMonth <= maxDate) {
                        setShowSkipAlert(true);
                    }
                }
            }

            setSelectedTime("");
            setShowModal(false);
        }
    };

    const handleRemoveSlot = (dateTime) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            selectedTimeSlots: prevFormData.selectedTimeSlots.filter((slot) => slot.dateTime !== dateTime),
        }));
    };

    const handleSkipMonth = (skip) => {
        setShowSkipAlert(false);
        if (!skip) {
            const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
            if (nextMonth <= maxDate) {
                setCurrentMonth(nextMonth);
            }
        }
    };

    const events = formData.selectedTimeSlots.map((slot) => ({
        title: `${slot.startTime} - ${slot.endTime}`,
        start: new Date(slot.date),
        end: new Date(slot.date),
        allDay: false,
    }));

    return (
        <div className="p-3">
        <h5 className="fw-bold">
            Schedule Your Calendar <span className="text-danger fw-bold">*</span>
        </h5>
        <div className="text-xs text-danger fw-bold" style={{ fontSize: "0.9rem" }}>
            <span className="text-danger fw-bold">*</span> You can schedule your availability for B2B meetings by selecting
            dates and times that are convenient for you.
            <span> Based on the slots you choose, counterparts will be able to request appointments.</span>
            <div>
                <div className="fw-bold text-black text-sm">Here are the scheduling options:</div>
                <ul>
                    <li>A maximum of 2 slots per day</li>
                    <li>A minimum 8 slots per Month</li>
                    <li>Slots can only be booked from Monday to Friday</li>
                    <li>Slot timings will follow Indian Standard Time (IST) Zone.</li>
                </ul>
            </div>
        </div>
        <hr />
            <div className="mb-4">
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 500 }}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    min={minDate}
                    max={maxDate}
                    dayPropGetter={dayPropGetter}
                    date={currentMonth}
                    onNavigate={date => setCurrentMonth(date)}
                />
            </div>

            {formData.selectedTimeSlots.length > 0 && (
                <div className="mt-4">
                    <h6 className="fw-bold">Selected Time Slots:</h6>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Duration</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formData.selectedTimeSlots
                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                .map((slot, index) => (
                                    <tr key={index}>
                                        <td>{format(new Date(slot.date), "MMMM d, yyyy")}</td>
                                        <td>{`${slot.startTime} - ${slot.endTime}`}</td>
                                        <td>{`${slot.duration} minutes`}</td>
                                        <td>
                                            <Button 
                                                variant="danger" 
                                                size="sm" 
                                                onClick={() => handleRemoveSlot(slot.dateTime)}
                                            >
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Select Time Slot</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Date Selected: {selectedDate ? format(selectedDate, "MMMM d, yyyy") : ""}</p>
                    <Form.Group>
                        <Form.Label>Select Time</Form.Label>
                        <Form.Select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                            <option value="">Choose a time slot</option>
                            {timeSlots.map((time, index) => (
                                <option key={index} value={time}>
                                    {time}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddTimeSlot}>
                        Add Time Slot
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showSkipAlert} onHide={() => setShowSkipAlert(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Month Complete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    You have completed booking 8 slots for {format(currentMonth, "MMMM yyyy")}. 
                    Would you like to skip booking slots for the remaining months?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => handleSkipMonth(true)}>
                        Skip Remaining Months
                    </Button>
                    <Button variant="primary" onClick={() => handleSkipMonth(false)}>
                        Continue to Next Month
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default ScheduleCalender;